import { useState, useRef, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';
import { GLOSSARY } from '../lib/glossary';

/**
 * Ícono (?) que muestra la definición de un término del glossary en un popover.
 * Se posiciona automáticamente arriba/abajo según el espacio disponible.
 */
export default function TermTooltip({ termKey, className = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const g = GLOSSARY[termKey];

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  if (!g) return null;

  return (
    <span ref={ref} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-[#9CA3AF] text-[#6B7280] hover:border-[#143A34] hover:text-[#143A34] hover:bg-[#F0FBF5] transition-colors align-middle ml-1"
        aria-label={`¿Qué es ${g.term}?`}
        data-testid={`term-tooltip-${termKey}`}
      >
        <HelpCircle size={11} strokeWidth={2} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute z-30 top-full left-0 mt-1.5 w-[340px] max-w-[85vw] bg-white border border-[#1C2329] shadow-xl p-4 text-left"
          data-testid={`term-tooltip-popover-${termKey}`}
        >
          <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#6B7280]">
            {g.law || 'Ley 21.719'}
          </div>
          <div className="mt-1 text-[14px] font-semibold text-[#1C2329] leading-tight">{g.term}</div>
          <div className="mt-2 text-[12.5px] text-[#374151] leading-relaxed">{g.what}</div>
          {g.why && (
            <div className="mt-2 text-[12px] text-[#6B7280] leading-relaxed">
              <b className="text-[#1C2329]">¿Por qué importa?</b> {g.why}
            </div>
          )}
          {g.examples && g.examples.length > 0 && (
            <div className="mt-2 pt-2 border-t border-[#E5E7EB]">
              <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#6B7280] mb-1">Ejemplos</div>
              <ul className="text-[11.5px] text-[#374151] space-y-1 list-disc pl-4">
                {g.examples.slice(0, 2).map((ex, i) => <li key={i}>{ex}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </span>
  );
}

// Detección heurística de términos técnicos en un texto plano.
// Devuelve una lista de nodos React alternando texto y tooltips.
const DETECTION_MAP = [
  { pattern: /\bRAT\b/g, key: 'rat' },
  { pattern: /\bARCO\+?\b/g, key: 'arco' },
  { pattern: /\bDPIA\b/g, key: 'dpia' },
  { pattern: /\bDPO\b/g, key: 'dpo' },
  { pattern: /\bDPAs?\b/g, key: 'dpa' },
  { pattern: /\bCMP\b/g, key: 'cookies' },
  { pattern: /\bbase legal\b/gi, key: 'base_legal' },
  { pattern: /\bdatos sensibles\b/gi, key: 'datos_sensibles' },
  { pattern: /\bconsentimiento\b/gi, key: 'consentimiento' },
];

export function withTermTooltips(text) {
  if (!text) return text;
  // Encontrar TODOS los matches con su posición
  const hits = [];
  for (const { pattern, key } of DETECTION_MAP) {
    if (!GLOSSARY[key]) continue;
    pattern.lastIndex = 0;
    let m;
    while ((m = pattern.exec(text)) !== null) {
      hits.push({ start: m.index, end: m.index + m[0].length, key, matched: m[0] });
    }
  }
  if (hits.length === 0) return text;
  // Ordenar y deduplicar overlaps (quedarse con el primero)
  hits.sort((a, b) => a.start - b.start);
  const clean = [];
  let lastEnd = -1;
  for (const h of hits) {
    if (h.start >= lastEnd) { clean.push(h); lastEnd = h.end; }
  }
  // Construir nodos
  const nodes = [];
  let cursor = 0;
  clean.forEach((h, i) => {
    if (h.start > cursor) nodes.push(text.slice(cursor, h.start));
    nodes.push(
      <span key={`t-${i}`} className="inline-flex items-baseline">
        <span className="font-medium text-[#1C2329]">{h.matched}</span>
        <TermTooltip termKey={h.key} />
      </span>,
    );
    cursor = h.end;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}
